import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Settings, ArrowUpFromLine, User, DollarSign, LayoutGrid, Award, Clock, ArrowDownToLine, Wrench, FileUp, UserCheck, BarChart3, KeyRound, HeartPulse, MapPin, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const allSetupItems = [
  {
    icon: <Settings className="w-10 h-10" />,
    title: 'Settings',
    description: 'Tweak and change Resto MP to fit your business model.',
    link: '/setup/settings',
    permission: 'setup'
  },
  {
    icon: <ArrowUpFromLine className="w-10 h-10" />,
    title: 'Franchise Locations',
    description: 'Manage the franchise locations associated to your account.',
    link: '/setup/franchise-locations',
    permission: 'setup'
  },
  {
    icon: <User className="w-10 h-10" />,
    title: 'Users & Employees',
    description: 'Setup and manage your application users, employees, employee pay and other attribute of the workforce.',
    link: '/setup/users-employees',
    permission: 'management'
  },
  {
    icon: <DollarSign className="w-10 h-10" />,
    title: 'Project Expenses',
    description: 'Manage the various expenses you will need to track and report.',
    link: '/setup/project-expenses',
    permission: 'setup'
  },
  {
    icon: <LayoutGrid className="w-10 h-10" />,
    title: 'Project Types',
    description: 'Set up your project types and commission structure.',
    link: '/setup/project-types',
    permission: 'setup'
  },
  {
    icon: <Award className="w-10 h-10" />,
    title: 'Certifications',
    description: 'Set up your certifications that are associated with your employees.',
    link: '/setup/certifications',
    permission: 'setup'
  },
  {
    icon: <Clock className="w-10 h-10" />,
    title: 'Export My Data',
    description: 'Export all of your data to Microsoft Excel.',
    link: '/setup/export-data',
    permission: 'setup'
  },
  {
    icon: <ArrowDownToLine className="w-10 h-10" />,
    title: 'Project Sources',
    description: 'Source selection for your projects.',
    link: '/setup/project-sources',
    permission: 'setup'
  },
  {
    icon: <Wrench className="w-10 h-10" />,
    title: 'Dev Testing',
    description: 'Tools for Developer Testing.',
    link: '/setup/dev-testing',
    permission: 'setup'
  },
  {
    icon: <FileUp className="w-10 h-10" />,
    title: 'Import Projects',
    description: 'Bulk-import legacy projects (and their customers) from a RestorePM .xlsx export. Admin-only.',
    link: '/setup/import-projects',
    permission: 'admin'
  },
  {
    icon: <FileUp className="w-10 h-10" />,
    title: 'Import Projects v2 (Closed + Dead)',
    description: 'Bulk-import legacy Closed and Dead projects from the flat RestorePM CSV export. Includes labor & expense entries, parses PM splits, auto-creates inactive skeleton employees for legacy names. Admin-only.',
    link: '/setup/import-projects-v2',
    permission: 'admin'
  },
  {
    icon: <FileUp className="w-10 h-10" />,
    title: 'Import Labor & Expenses',
    description: 'Upload a screenshot of a Labor Summary or Project Expenses table — the server reads it with vision OCR, you confirm the rows, then commit to the project. Admin-only.',
    link: '/setup/import-labor-expenses',
    permission: 'admin'
  },
  {
    icon: <UserCheck className="w-10 h-10" />,
    title: 'Normalize Employee Names',
    description: 'One-time cleanup: scan every project and rewrite legacy name variants ("K.Pinto", "Kelita Pinto", etc.) to the canonical "Last, First" format from the employees directory. Dry-run first. Admin-only.',
    link: '/setup/normalize-names',
    permission: 'admin'
  },
  {
    icon: <BarChart3 className="w-10 h-10" />,
    title: 'Insights',
    description: 'Dashboard of project, labor, commission, and lifecycle statistics across the entire system. Sources, boards, territories, PMs, profit, expenses, and more. Admin-only.',
    link: '/setup/insights',
    permission: 'admin'
  },
  {
    icon: <KeyRound className="w-10 h-10" />,
    title: 'Repair Customer IDs',
    description: 'Scan for customers whose ID doesn\'t match the prefix of their existing project IDs (a legacy-import quirk) and bulk-rename them in one batch. Linked project IDs are atomically renumbered. Requires the "Update Customer IDs" permission.',
    link: '/setup/repair-customer-ids',
    permission: 'update_customer_ids'
  },
  {
    icon: <HeartPulse className="w-10 h-10" />,
    title: 'Data Health',
    description: 'Read-only scan of the database for legacy issues — trailing-zero invoice numbers, malformed dates, orphaned references, denormalization drift, name-format variants, missing required fields, and stale customer IDs. Re-run anytime. Admin-only.',
    link: '/setup/data-health',
    permission: 'admin'
  },
  {
    icon: <MapPin className="w-10 h-10" />,
    title: 'Map',
    description: 'Plot every In-Progress and Accounts-Receivable project on a map of the United States. Filter by status, filter by job type, search by customer or address, and click any pin to jump straight to the project.',
    link: '/setup/map',
    permission: 'setup'
  },
  {
    icon: <Trash2 className="w-10 h-10" />,
    title: 'Dedup Tools',
    description: 'Clean up duplicate projects (same project_id) and merge two customer records into one — reassigns their projects, deletes the source customer. Also includes a read-only audit that lists likely-duplicate customers across the v1/v2 imports. Every destructive action has a dry-run + confirmation. Admin-only.',
    link: '/setup/dedup-tools',
    permission: 'admin'
  }
];

const Setup = () => {
  const { hasPermission, user } = useAuth();
  const setupItems = allSetupItems.filter(item => {
    if (item.permission === 'admin') return !!user?.is_admin;
    return hasPermission(item.permission);
  });
  return (
    <Layout>
      <div className="p-4 lg:p-8">
        <div className="mb-6">
          <h2 className="text-lg text-[#5B8DB8] font-light">Administration</h2>
          <h1 className="text-3xl text-gray-700 font-light italic">General Setup</h1>
        </div>

        <div className="bg-white rounded shadow p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {setupItems.map((item, index) => (
              <Link
                key={index}
                to={item.link}
                className="flex items-start gap-4 p-4 hover:bg-gray-50 rounded transition-colors group"
                data-testid={`setup-item-${item.title.toLowerCase().replace(/[&\s]+/g, '-')}`}
              >
                <div className="text-gray-800 flex-shrink-0 mt-1">
                  {item.icon}
                </div>
                <div>
                  <h3 className="text-[#5B8DB8] font-normal group-hover:underline">{item.title}</h3>
                  <p className="text-gray-500 text-sm mt-1">{item.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Setup;
